import { apiGet, requestPost } from 'service/api';
import { put, race, select, take, takeEvery } from 'typed-redux-saga';
import { Api, apiAsesAssessment } from 'service/api/constants';
import {
  GetAssessmentListFiltersRequest,
  AssessmentListRequest,
} from 'types/Endpoints/Assessments';
import { isError, isNetworkError } from 'lib/isError';
import {
  ALERT,
  ALERT_ERROR,
  CATCH_ERROR,
  HIDE_BLOCKING_LOADER,
  NAVIGATE_MODAL,
  NAVIGATE_MODAL_BACK,
  SHOW_BLOCKING_LOADER,
} from 'store/actions';
import { doAseLoadDataFromNewApi } from 'store/assessments/actions';
import { AssessmentPhpResponse } from 'types/Endpoints/AssessmentsPhp';
import { navigate } from 'components/Navigation/navigationRef';
import { ModalScreens, Screens, Stacks } from 'components/Navigation/types';
import {
  doAsesListClearOrganisations as doAsesFilterClearOrganisations,
  doAsesListLoad,
  doAsesAssessmentLoad,
  doAsesListFilterLoad,
  doAsesListFilterOpenModal,
  doAsesListSet,
  doAsesListSetData,
  doAsesListSetFilter,
  doAsesListSetFilterDraft,
  doAsesListSetFilterOptions,
  doAsesListSetFilterOptionsDraft,
  doAsesListSetItem,
  doAsesListSetLoadStatus,
  doneAsesListFilterLoad,
  failAsesListFilterLoad,
  doAsesListFilterOpenOrganisationSelect,
} from './actions';
import {
  selectAsesListFilterLoadStatus,
  selectAsesListFilter,
  selectAsesListFilterDraft,
  selectAsesListFilterOptions,
  selectAsesListFilterOptionsDraft,
  selectAsesListFilterOrganisationIds,
  selectAsesListPage,
} from './selectors';
import { sagaSelect } from 'store/sagaHelpers';
import { sortBy } from 'lodash';
import delay from 'delay';
import { randomKey } from 'lib/riskbase';

export const asesSideEffects = [
  takeEvery(doAsesListFilterLoad, function* f(action) {
    const { setDraft, organisationIds } = action.payload;
    const finalOrganisationIds =
      organisationIds === null
        ? yield* select(selectAsesListFilterOrganisationIds)
        : organisationIds;
    try {
      const response = yield* requestPost<GetAssessmentListFiltersRequest>({
        api: Api.NODE,
        url: '/assessments/v2/listfilters',
        params: {},
        body: { organisationIds: finalOrganisationIds },
      });
      if (setDraft) {
        yield* put(doAsesListSetFilterOptionsDraft(response.filterOptions));
        yield* put(
          doAsesListSetFilterDraft({
            data: { organisationIds: finalOrganisationIds },
            append: true,
          }),
        );
      } else {
        yield* put(doAsesListSetFilterOptions(response.filterOptions));
      }
      yield* put(doneAsesListFilterLoad());
    } catch (error) {
      yield* put(CATCH_ERROR({ error }));
      if (isError(error)) {
        yield* put(failAsesListFilterLoad({ error }));
      }
    }
  }),

  takeEvery(doAsesListFilterOpenOrganisationSelect, function* f(action) {
    const { getDraft, openFilterAfterSelect } = action.payload;
    const filterOptions = yield* select(
      getDraft ? selectAsesListFilterOptionsDraft : selectAsesListFilterOptions,
    );
    const filter = yield* select(
      getDraft ? selectAsesListFilterDraft : selectAsesListFilter,
    );
    let wasDismissed = true;
    yield* sagaSelect({
      selectConfig: {
        title: 'Organisations',
        text:
          filterOptions.organisationRequired && !filter.organisationIds.length
            ? 'Choose at least one organisation to get started'
            : 'Changing the selected organisation may alter which other filters are available',
        multiSelect: true,
        searchable: true,
        enableSelectAll: filterOptions.organisations.length <= 20,
        options: sortBy(filterOptions.organisations, 'name').map((o) => ({
          internalValue: o.id,
          name: o.name,
        })),
        value: filter.organisationIds,
        showQuickChoose: true,
      },
      onSelect: function* f(value) {
        wasDismissed = false;
        if (filterOptions.organisationRequired && !value.length) {
          yield* put(
            ALERT({
              title: 'Choose Organisation',
              text: 'Please choose at least one organisation to get started',
            }),
          );
          return false;
        }
        yield* put(SHOW_BLOCKING_LOADER());
        yield* put(
          doAsesListFilterLoad({
            setDraft: true,
            organisationIds: value as string[],
          }),
        );
        const taken = yield* race({
          fail: take(failAsesListFilterLoad),
          done: take(doneAsesListFilterLoad),
        });
        yield* put(HIDE_BLOCKING_LOADER());
        if (taken.fail) {
          yield* put(ALERT_ERROR({ error: taken.fail.payload.error }));
          return false;
        }
        yield* put(
          doAsesListSetFilterDraft({
            data: { organisationIds: value as string[] },
            append: true,
          }),
        );
        return true;
      },
    });
    if (wasDismissed) {
      return;
    }
    if (openFilterAfterSelect) {
      yield delay(10);
      yield* put(doAsesListFilterOpenModal({ setDraft: true }));
    }
  }),

  takeEvery(doAsesFilterClearOrganisations, function* f() {
    yield* put(SHOW_BLOCKING_LOADER());
    yield* put(
      doAsesListFilterLoad({
        setDraft: true,
        organisationIds: [],
      }),
    );
    const taken = yield* race({
      fail: take(failAsesListFilterLoad),
      done: take(doneAsesListFilterLoad),
    });
    yield* put(HIDE_BLOCKING_LOADER());
    if (taken.fail) {
      yield* put(ALERT_ERROR({ error: taken.fail.payload.error }));
      return false;
    }
  }),

  takeEvery(doAsesListFilterOpenModal, function* f(action) {
    const { setDraft: useDraft } = action.payload;
    const isListFilterLoadStatus = yield* select(
      selectAsesListFilterLoadStatus,
    );
    if (isListFilterLoadStatus !== 'loaded') {
      yield* put(SHOW_BLOCKING_LOADER());
      if (isListFilterLoadStatus !== 'loading') {
        yield* put(
          doAsesListFilterLoad({
            setDraft: false,
            organisationIds: (yield* select(selectAsesListFilter))
              .organisationIds,
          }),
        );
      }
      const taken = yield* race({
        fail: take(failAsesListFilterLoad),
        done: take(doneAsesListFilterLoad),
      });
      yield* put(HIDE_BLOCKING_LOADER());
      if (taken.fail) {
        yield* put(ALERT_ERROR({ error: taken.fail.payload.error }));
        return;
      }
    }
    if (!useDraft) {
      // Apply filter options and filter to draft
      yield* put(
        doAsesListSetFilterOptionsDraft(
          yield* select(selectAsesListFilterOptions),
        ),
      );
      yield* put(
        doAsesListSetFilterDraft({
          data: yield* select(selectAsesListFilter),
          append: false,
        }),
      );
    }
    const filterOptions = yield* select(selectAsesListFilterOptionsDraft);
    if (filterOptions.organisationRequired) {
      yield* put(
        doAsesListFilterOpenOrganisationSelect({
          getDraft: true,
          openFilterAfterSelect: true,
        }),
      );
    } else {
      yield* put(
        NAVIGATE_MODAL({
          screen: ModalScreens.AsesListFilter,
          params: {},
        }),
      );
    }
  }),

  takeEvery(doAsesListLoad, function* f(action) {
    const { type, useDraftFilter, append, useBlockingLoader } = action.payload;

    if ((yield* select(selectAsesListFilterLoadStatus)) !== 'loaded') {
      yield* put(doAsesListFilterOpenModal({ setDraft: false }));
      return;
    }

    // Open organisation selector if one is required
    if (
      (yield* select(
        useDraftFilter
          ? selectAsesListFilterOptionsDraft
          : selectAsesListFilterOptions,
      )).organisationRequired
    ) {
      yield* put(
        doAsesListFilterOpenOrganisationSelect({
          getDraft: false,
          openFilterAfterSelect: true,
        }),
      );
      return;
    }

    const currentListPage = yield* select(selectAsesListPage);
    const blockingLoaderKey = randomKey();

    if (useBlockingLoader) {
      yield* put(SHOW_BLOCKING_LOADER(blockingLoaderKey));
    }
    yield* put(
      doAsesListSetLoadStatus({
        status: 'loading',
        type: type === 'pullToRefresh' ? 'pullToRefresh' : 'filter',
      }),
    );
    try {
      const finalListPage = append ? currentListPage + 1 : 1;
      const response = yield* requestPost<AssessmentListRequest>(
        {
          api: Api.NODE,
          url: '/assessments/v2/list',
          params: {},
          query: {
            page: finalListPage,
          },
          body: {
            includeTotal: !append,
            filter: yield* select(
              useDraftFilter ? selectAsesListFilterDraft : selectAsesListFilter,
            ),
          },
        },
        {
          abortKey: 'asesListLoad',
        },
      );
      yield* put(doAsesListSetData(response.data));
      yield* put(
        doAsesListSet({
          list: response.list,
          page: finalListPage,
          totalCount: response.totalCount,
          append: !!append,
        }),
      );
      if (useDraftFilter) {
        yield* put(
          doAsesListSetFilterOptions(
            yield* select(selectAsesListFilterOptionsDraft),
          ),
        );
        yield* put(
          doAsesListSetFilter({
            data: yield* select(selectAsesListFilterDraft),
            append: false,
            loadList: false,
          }),
        );
      }
      yield* put(HIDE_BLOCKING_LOADER(blockingLoaderKey));
      yield* put(NAVIGATE_MODAL_BACK([ModalScreens.AsesListFilter]));
      yield* put(doAsesListSetLoadStatus({ status: 'loaded' }));
    } catch (error) {
      yield* put(HIDE_BLOCKING_LOADER(blockingLoaderKey));
      if (isNetworkError(error) && !error.isCancelled) {
        yield* put(ALERT_ERROR({ error, action }));
        yield* put(
          doAsesListSetLoadStatus({
            status: 'error',
          }),
        );
      }
    }
  }),

  takeEvery(doAsesListSetFilter, function* f(action) {
    const { loadList } = action.payload;
    if (loadList) {
      yield* put(
        doAsesListLoad({
          append: false,
          useDraftFilter: false,
          useBlockingLoader: loadList.useBlockingLoader,
        }),
      );
    }
  }),

  takeEvery(doAsesAssessmentLoad, function* f(action) {
    const { id } = action.payload;
    try {
      yield* put(SHOW_BLOCKING_LOADER());
      const response = yield* apiGet<AssessmentPhpResponse>(
        apiAsesAssessment(id),
      );
      yield* put(doAseLoadDataFromNewApi({ response }));
      yield* put(
        doAsesListSetItem({
          id,
          item: {
            ...response.listItem,
            property: {
              id: response.listItem.asset.id,
              name: response.listItem.asset.name,
              reference: response.listItem.asset.reference || '',
              customReference: response.listItem.asset.customReference || null,
            },
          },
        }),
      );
      navigate(Stacks.ROOT, Screens.ROOT_ASSESSMENT, {
        assessmentId: response.id,
      });
    } catch (error) {
      if (isError(error)) {
        yield* put(ALERT_ERROR({ error }));
      }
      yield* put(CATCH_ERROR({ error }));
    }
    yield* put(HIDE_BLOCKING_LOADER());
  }),
];
